import { call } from '@advanza/api'
import { useEffect, useMemo, useState } from 'react'
import type { FilterAnswer, Option, Question } from './types'

/** requiredOptionsMap: { [dependsOnQuestionId]: requiredOptionIds[] } */
export interface QuestionWithDeps extends Question {
    requiredOptionsMap: Record<string, number[]>
}

/** Raw question shape from the request-form configuration endpoint. */
interface RawServiceQuestion extends Omit<Question, 'options'> {
    options?: Option[]
    requiredOptionsMap?: Record<string, number[]>
}

interface RequestFormConfigurationResponse {
    config?: { questions?: Record<string, RawServiceQuestion> }
}

interface ServiceFiltersState {
    allFilterableQuestions: QuestionWithDeps[]
    isLoading: boolean
}

export function useServiceFilters(serviceUrl: string | undefined, selectedAnswers: FilterAnswer[]) {
    const [state, setState] = useState<ServiceFiltersState>({
        allFilterableQuestions: [],
        isLoading: false,
    })

    useEffect(() => {
        if (!serviceUrl) {
            setState({ allFilterableQuestions: [], isLoading: false })
            return
        }

        setState((prev) => ({ ...prev, isLoading: true }))

        call(`request-form/getRequestFormConfiguration/quote/${serviceUrl}`, { query: {} })
            .then((response: RequestFormConfigurationResponse) => {
                const questionsMap = response.config?.questions || {}

                const filterableQuestions: QuestionWithDeps[] = Object.values(questionsMap)
                    .filter((q) => q.filterable)
                    .sort((a, b) => a.order_value - b.order_value)
                    .map((q) => {
                        const options = q.options
                            ? [...q.options].sort((a, b) => a.order_value - b.order_value)
                            : []
                        return {
                            ...q,
                            options,
                            requiredOptionsMap: q.requiredOptionsMap || {},
                        }
                    })

                setState({ allFilterableQuestions: filterableQuestions, isLoading: false })
            })
            .catch(() => {
                setState((prev) => ({ ...prev, isLoading: false }))
            })
    }, [serviceUrl])

    // Filter to only visible questions based on selected answers and dependencies.
    // Only enforce deps on other filterable questions — non-filterable deps are ignored
    // since the user can't select them in the debugger.
    const visibleQuestions = useMemo(() => {
        const filterableIds = new Set(state.allFilterableQuestions.map((q) => q.question_id))

        const selectedByQuestion = new Map<number, number[]>()
        for (const answer of selectedAnswers) {
            selectedByQuestion.set(answer.questionId, answer.options)
        }

        return state.allFilterableQuestions.filter((q) => {
            const deps = Object.entries(q.requiredOptionsMap)
            if (deps.length === 0) return true

            return deps.every(([depQuestionId, requiredOptionIds]) => {
                // Skip deps on non-filterable questions — we can't control those
                if (!filterableIds.has(Number(depQuestionId))) return true

                const selected = selectedByQuestion.get(Number(depQuestionId)) || []
                return requiredOptionIds.some((reqId) => selected.includes(Number(reqId)))
            })
        })
    }, [state.allFilterableQuestions, selectedAnswers])

    return {
        filterableQuestions: visibleQuestions,
        allFilterableQuestions: state.allFilterableQuestions,
        isLoading: state.isLoading,
    }
}
